import React, { useMemo } from "react";
import styled from "styled-components";
import { isArray, isBoolean, isNumber, isString, reduce } from "lodash";
import { ChevronDown, Filters as FiltersIcon } from "@sonder/ui/source/icons";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { DropdownContent } from "@sonder/ui/source/styled/components/DropdownContent";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import { Tag } from "@sonder/ui/source/styled/fragments/Tag";
import { useSegment } from "analytics/segment";
import { TagTypes, useGetTagsQuery } from "core/queries/GetTags";
import {
  FilterGroup,
  FilterGroupContainer,
} from "tasks/components/Filters/FilterGroup";
import { FilterGroupItem } from "tasks/components/Filters/FilterGroupItem";
import {
  FilterPair,
  arePriorityFiltersActive,
  areStatusFiltersActive,
  isDuringStayFilterActive,
} from "tasks/helpers";
import { useStore } from "tasks/store";
import { FilterDuringStay, FilterStatus } from "typings/globalGQLTypes";

const StyledAdditionalFilters = styled.div`
  display: flex;
  max-height: 20rem;
  max-width: 36rem;
  overflow: auto;
  padding: 2rem 1.5rem;
`;

const StackedFilterGroup = styled.div`
  display: block;
  ${FilterGroupContainer} + ${FilterGroupContainer} {
    margin-top: 1rem;
  }
`;

const StyledButton = styled(Button)`
  line-height: 1.5rem;
`;

const areTomTagFiltersActive = ({
  globalFilters,
  localFilters,
}: FilterPair) => {
  const results =
    !!localFilters.tomTags &&
    localFilters.tomTags.every(p =>
      globalFilters.tomTags
        ? !!globalFilters.tomTags.find(tag => tag === p)
        : false,
    );
  return results;
};

enum FilterTypes {
  array = "Array",
  bool = "Boolean",
  num = "Number",
  string = "String",
}

const getTypeof = (obj?: any[] | string) => {
  if (isArray(obj)) return FilterTypes.array;

  if (isBoolean(obj)) return FilterTypes.bool;

  if (isNumber(obj)) return FilterTypes.num;

  if (isString(obj)) return FilterTypes.string;

  return undefined;
};

const useSelectedFilterCount = (
  filtersToCompare = ["duringStay", "priority", "status", "tomTags"],
) => {
  const {
    state: { filters: globalFilters },
  } = useStore();
  const count = useMemo(
    () =>
      reduce(
        globalFilters,
        (result, filter, key) => {
          if (filter && filtersToCompare.includes(key))
            switch (getTypeof(filter)) {
              case FilterTypes.array:
                return result + filter.length;
              case FilterTypes.bool:
              case FilterTypes.num:
              case FilterTypes.string:
                return result + 1;
              default:
                break;
            }

          return result;
        },
        0,
      ),
    [globalFilters],
  );
  return count;
};

export const AdditionalFilters = () => {
  const { track } = useSegment();
  const selectedFilterCount = useSelectedFilterCount();
  const { data: squadData, loading: squadLoading } = useGetTagsQuery({
    variables: {
      tagType: TagTypes.Squad,
    },
  });
  const { data: globalData, loading: globalLoading } = useGetTagsQuery({
    variables: {
      tagType: TagTypes.Global,
    },
  });
  const squadTags = (squadData && squadData.tomTags) || [];
  const miscFilters = (
    <StackedFilterGroup>
      <FilterGroup label="Status">
        <FilterGroupItem
          filters={{ status: [FilterStatus.incomplete] }}
          isActive={areStatusFiltersActive}
          label="Incomplete"
          segmentId="status_incomplete"
        />
        <FilterGroupItem
          filters={{ status: [FilterStatus.complete] }}
          isActive={areStatusFiltersActive}
          label="Completed"
          segmentId="status_complete"
        />
      </FilterGroup>
      <FilterGroup label="Inquiry">
        <FilterGroupItem
          filters={{ duringStay: FilterDuringStay.during_stay }}
          isActive={isDuringStayFilterActive}
          label="During Stay"
          segmentId="inquiry_during_stay"
        />
      </FilterGroup>
      <FilterGroup label="Priority">
        <FilterGroupItem
          filters={{ priority: [1] }}
          isActive={arePriorityFiltersActive}
          label="P1"
          segmentId="priority_p1"
        />
        <FilterGroupItem
          filters={{ priority: [2] }}
          isActive={arePriorityFiltersActive}
          label="P2"
          segmentId="priority_p2"
        />
        <FilterGroupItem
          filters={{ priority: [3] }}
          isActive={arePriorityFiltersActive}
          label="P3"
          segmentId="priority_p3"
        />
        <FilterGroupItem
          filters={{ priority: [4] }}
          isActive={arePriorityFiltersActive}
          label="P4"
          segmentId="priority_p4"
        />
        <FilterGroupItem
          filters={{ priority: [5] }}
          isActive={arePriorityFiltersActive}
          label="P5"
          segmentId="priority_p5"
        />
      </FilterGroup>
    </StackedFilterGroup>
  );
  const squadFilters = (
    <FilterGroup label={TagTypes.Squad}>
      {!squadTags.length && squadLoading && <div>Loading...</div>}
      {squadTags && (
        <>
          <FilterGroupItem
            filters={{
              tomTags: ["-1"],
            }}
            isActive={areTomTagFiltersActive}
            key="NO_SQUAD"
            label="No Squad"
            segmentId="squadTag_NO_SQUAD"
          />
          {squadTags.map(tag => (
            <FilterGroupItem
              filters={{
                tomTags: [tag.id],
              }}
              isActive={areTomTagFiltersActive}
              key={tag.id}
              label={tag.name}
              segmentId={`squadTag_${tag.name}`}
            />
          ))}
        </>
      )}
    </FilterGroup>
  );
  const globalTags = (globalData && globalData.tomTags) || [];
  const globalTagFilters = (
    <FilterGroup label="Tags">
      {!globalTags.length && globalLoading && <div>Loading...</div>}
      {globalTags && (
        <>
          {globalTags
            .sort((a, b) => (a.name < b.name ? -1 : 1))
            .map(tag => (
              <FilterGroupItem
                filters={{
                  tomTags: [tag.id],
                }}
                isActive={areTomTagFiltersActive}
                key={tag.id}
                label={tag.name}
                segmentId={`globalTag_${tag.name}`}
              >
                <Tag filled>
                  <span>{tag.name}</span>
                </Tag>
              </FilterGroupItem>
            ))}
        </>
      )}
    </FilterGroup>
  );
  return (
    <Dropdown
      trigger={
        <StyledButton iconLeft iconRight noBorder>
          <FiltersIcon />
          {selectedFilterCount ? `(${selectedFilterCount})` : ""}
          <ChevronDown />
        </StyledButton>
      }
      onClose={() =>
        track({
          eventName: "view_header",
          action: "click",
          element: "filters_menu.dropdown_trigger",
          props: { nextValue: "closed" },
        })
      }
      onOpen={() =>
        track({
          eventName: "view_header",
          action: "click",
          element: "filters_menu.dropdown_trigger",
          props: { nextValue: "opened" },
        })
      }
    >
      {({ isOpen }) =>
        isOpen && (
          <DropdownContent align="center">
            <StyledAdditionalFilters>
              {miscFilters}
              {squadFilters}
              {globalTagFilters}
            </StyledAdditionalFilters>
          </DropdownContent>
        )
      }
    </Dropdown>
  );
};
